var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import * as React from 'react';
import { createInheritedProvider } from '@shapeable/utils';
import { PersonBioCell } from "../entities/person-bio-cell";
import { EntityTooltip } from "../entities/entity-tooltip";
import { EntityCard } from '../entities/entity-card';
import { mapValues } from 'lodash';
import { PersonIcon, EventIcon, OrganisationIcon, VideoIcon, OrganisationTypeIcon, TrendIcon, TopicIcon, SubTopicIcon, CitationIcon, EntityIcon } from '@shapeable/icons';
import { CitationCard } from '../entities/citation-card';
import { OrganisationBioCell } from "../entities/organisation-bio-cell";
import { titleCase } from "@shapeable/utils";
import pluralize from "pluralize";
import { useChallenges } from '../hooks/use-challenges';
import { useCitations } from '../hooks/use-citations';
import { useEconomies } from '../hooks/use-economies';
import { useEvents } from '../hooks/use-events';
import { useMenus } from '../hooks/use-menus';
import { useOrganisationTypes } from '../hooks/use-organisation-types';
import { useOrganisations } from '../hooks/use-organisations';
import { usePeople } from '../hooks/use-people';
import { usePosts } from '../hooks/use-posts';
import { useTrends } from '../hooks/use-trends';
import { useVideos } from '../hooks/use-videos';
import { useSubTopics } from '../hooks/use-sub-topics';
import { useTopics } from '../hooks/use-topics';
import { usePages } from '../hooks/use-pages';
import { PersonViewLayout } from "../entities/person-view-layout";
import { PersonMetaLayout } from "../entities/person-meta-layout";
import { PersonCell } from "../entities/person-cell";
import { EntityBanner } from "../entities/entity-banner";
import { OrganisationCell } from "../entities/organisation-cell";
import { OrganisationViewLayout } from "../entities/organisation-view-layout";
import { OrganisationMetaLayout } from "../entities/organisation-meta-head-layout";
import { PageViewLayout } from "../entities/page-view-layout";
import { PageMetaLayout } from "../entities/page-meta-layout";
import { useKeyIssues } from "../hooks/use-key-issues";
export var defaultEntityTypeConfig = {
  Person: {
    iconComponent: PersonIcon,
    cardComponent: PersonBioCell,
    data: usePeople,
    explorer: {
      isAsideSticky: false,
      stickyTopOffset: 64,
      asideMaxWidth: 800,
      bannerComponent: PersonCell,
      contentComponent: PersonViewLayout,
      metaHeadComponent: PersonMetaLayout
    }
  },
  Organisation: {
    iconComponent: OrganisationIcon,
    cardComponent: OrganisationBioCell,
    data: useOrganisations,
    explorer: {
      isAsideSticky: false,
      stickyTopOffset: 64,
      asideMaxWidth: 800,
      bannerComponent: OrganisationCell,
      contentComponent: OrganisationViewLayout,
      metaHeadComponent: OrganisationMetaLayout
    }
  },
  OrganisationType: {
    iconComponent: OrganisationTypeIcon,
    data: useOrganisationTypes
  },
  Trend: {
    iconComponent: TrendIcon,
    data: useTrends,
    explorer: {
      stickyTopOffset: 64,
      asideMaxWidth: 800,
      bannerComponent: EntityBanner
    }
  },
  Topic: {
    iconComponent: TopicIcon,
    data: useTopics,
    explorer: {
      stickyTopOffset: 64,
      asideMaxWidth: 800,
      bannerComponent: EntityBanner
    }
  },
  SubTopic: {
    iconComponent: SubTopicIcon,
    data: useSubTopics,
    explorer: {
      stickyTopOffset: 64,
      asideMaxWidth: 800,
      bannerComponent: EntityBanner
    }
  },
  Citation: {
    iconComponent: CitationIcon,
    cardComponent: CitationCard,
    data: useCitations
  },
  Economy: {
    data: useEconomies,
    explorer: {
      bannerComponent: EntityBanner
    }
  },
  Page: {
    data: usePages,
    explorer: {
      bannerComponent: EntityBanner,
      contentComponent: PageViewLayout,
      metaHeadComponent: PageMetaLayout
    }
  },
  Post: {
    data: usePosts
  },
  Event: {
    data: useEvents,
    iconComponent: EventIcon,
    explorer: {
      bannerComponent: EntityBanner
    }
  },
  Challenge: {
    data: useChallenges,
    explorer: {
      bannerComponent: EntityBanner
    }
  },
  Innovation: {
    explorer: {
      bannerComponent: EntityBanner
    }
  },
  Catalyst: {
    explorer: {
      bannerComponent: EntityBanner
    }
  },
  CommodityGroup: {
    explorer: {
      bannerComponent: EntityBanner
    }
  },
  KeyIssue: {
    data: useKeyIssues,
    explorer: {
      bannerComponent: EntityBanner
    }
  },
  Menu: {
    data: useMenus
  },
  Team: {
    explorer: {
      bannerComponent: EntityBanner
    }
  },
  Video: {
    data: useVideos,
    iconComponent: VideoIcon
  }
};
export var EntityTypeConfigContext = React.createContext(defaultEntityTypeConfig);
export var EntityTypeConfigProvider = createInheritedProvider(EntityTypeConfigContext);
export var useEntityTypeDefinitions = function () {
  var context = React.useContext(EntityTypeConfigContext);
  return React.useMemo(function () {
    return mapValues(context, function (item, name) {
      var label = titleCase(name);
      var data = function () {
        return [];
      };
      return __assign({
        label: label,
        pluralLabel: pluralize.plural(label),
        iconComponent: EntityIcon,
        cardComponent: EntityCard,
        tooltipComponent: EntityTooltip,
        data: data
      }, item);
    });
  }, [context]);
};
export var useEntityTypeDefinition = function (entityTypeName) {
  return useEntityTypeDefinitions()[entityTypeName];
};
export function useEntityTypeData(entityTypeName) {
  var _a;
  return ((_a = useEntityTypeDefinition(entityTypeName)) === null || _a === void 0 ? void 0 : _a.data()) || [];
}
;